import React, { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { tweetsAPI, scheduleAPI } from '../api';
import { format } from 'date-fns';

function CharCounter({ text, max = 280 }) {
  const len = text.length;
  const cls = len > max ? 'over' : len > max * 0.9 ? 'warn' : '';
  return <div className={`char-counter ${cls}`}>{len} / {max}</div>;
}

function TweetModal({ tweet, onClose, onSaved }) {
  const [content, setContent] = useState(tweet?.content || '');
  const [tags, setTags] = useState(tweet?.tags || '');
  const [notes, setNotes] = useState(tweet?.notes || '');
  const [status, setStatus] = useState(tweet?.status || 'draft');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) return toast.error('内容を入力してください');
    if (content.length > 280) return toast.error('280文字を超えています');
    setSaving(true);
    try {
      if (tweet?.id) {
        await tweetsAPI.update(tweet.id, { content, tags, notes, status });
        toast.success('更新しました');
      } else {
        await tweetsAPI.create({ content, tags, notes, status });
        toast.success('保存しました');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{tweet?.id ? 'ツイート編集' : 'ツイート追加'}</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="form-group">
          <label className="form-label">ツイート内容</label>
          <textarea
            className="textarea"
            value={content}
            onChange={e => setContent(e.target.value)}
            placeholder="ツイートを入力... (280文字以内)"
            rows={6}
          />
          <CharCounter text={content} />
        </div>

        <div className="form-group">
          <label className="form-label">タグ（カンマ区切り）</label>
          <input className="input" value={tags} onChange={e => setTags(e.target.value)} placeholder="例: フリーランス, エンジニア" />
        </div>

        <div className="form-group">
          <label className="form-label">メモ</label>
          <input className="input" value={notes} onChange={e => setNotes(e.target.value)} placeholder="内部メモ（投稿されません）" />
        </div>

        <div className="form-group">
          <label className="form-label">ステータス</label>
          <select className="select" value={status} onChange={e => setStatus(e.target.value)}>
            <option value="draft">下書き</option>
            <option value="ready">投稿準備完了</option>
          </select>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ScheduleModal({ tweet, onClose, onScheduled }) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [saving, setSaving] = useState(false);

  const minDate = new Date(Date.now() + 60000);
  const minStr = format(minDate, "yyyy-MM-dd'T'HH:mm");

  const handleSchedule = async () => {
    if (!scheduledAt) return toast.error('日時を選択してください');
    setSaving(true);
    try {
      await scheduleAPI.create({
        content: tweet.content,
        scheduled_at: scheduledAt,
        tweet_id: tweet.id,
      });
      toast.success('スケジュール設定しました');
      onScheduled();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.error || 'エラーが発生しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">📅 スケジュール投稿</div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="card" style={{ marginBottom: 16, background: 'var(--bg-secondary)' }}>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 8 }}>投稿内容</div>
          <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{tweet.content}</div>
        </div>

        <div className="form-group">
          <label className="form-label">投稿日時</label>
          <input
            type="datetime-local"
            className="input"
            value={scheduledAt}
            min={minStr}
            onChange={e => setScheduledAt(e.target.value)}
          />
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn btn-ghost" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSchedule} disabled={saving}>
            {saving ? '設定中...' : 'スケジュール設定'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function TweetLibrary() {
  const [tweets, setTweets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [editTweet, setEditTweet] = useState(null);
  const [showAdd, setShowAdd] = useState(false);
  const [scheduleTweet, setScheduleTweet] = useState(null);
  const [postingId, setPostingId] = useState(null);

  const loadTweets = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus !== 'all') params.status = filterStatus;
      const res = await tweetsAPI.getAll(params);
      setTweets(res.data);
    } catch (e) {
      toast.error('読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [search, filterStatus]);

  useEffect(() => { loadTweets(); }, [loadTweets]);

  const handleDelete = async (id) => {
    if (!window.confirm('削除しますか？')) return;
    await tweetsAPI.delete(id);
    toast.success('削除しました');
    loadTweets();
  };

  const handlePostNow = async (tweet) => {
    if (!window.confirm('今すぐ投稿しますか？')) return;
    setPostingId(tweet.id);
    try {
      await tweetsAPI.postNow(tweet.id);
      toast.success('投稿しました！');
      loadTweets();
    } catch (e) {
      toast.error(e.response?.data?.error || '投稿に失敗しました');
    } finally {
      setPostingId(null);
    }
  };

  const statusCount = {
    all: tweets.length,
  };

  return (
    <div>
      <div className="page-header">
        <div className="page-title">📚 ツイートライブラリ</div>
        <div className="page-sub">ツイートの管理・即時投稿・スケジュール設定</div>
      </div>

      <div className="toolbar">
        <input
          className="input"
          style={{ width: 220 }}
          placeholder="🔍 検索..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
          <option value="all">すべて</option>
          <option value="draft">下書き</option>
          <option value="ready">準備完了</option>
          <option value="posted">投稿済み</option>
        </select>
        <div className="toolbar-spacer" />
        <button className="btn btn-primary" onClick={() => setShowAdd(true)}>
          ＋ ツイート追加
        </button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>読み込み中...</div>
      ) : tweets.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📭</div>
          <div className="empty-state-text">ツイートがありません<br /><span style={{fontSize:12}}>「ツイート追加」またはAI生成から登録できます</span></div>
        </div>
      ) : (
        tweets.map(tweet => (
          <div key={tweet.id} className="tweet-card">
            <div className="tweet-card-content">{tweet.content}</div>
            <div className="tweet-card-meta">
              <span className={`badge badge-${tweet.status}`}>
                {{ draft: '下書き', ready: '準備完了', posted: '投稿済み' }[tweet.status] || tweet.status}
              </span>
              {tweet.tags && tweet.tags.split(',').map((t, i) => (
                <span key={i} className="tweet-tag">#{t.trim()}</span>
              ))}
              <span style={{ fontSize: 11, color: 'var(--text-muted)', marginLeft: 'auto' }}>
                {tweet.created_at?.slice(0, 16)}
              </span>
            </div>
            <div className="tweet-card-actions" style={{ marginTop: 10 }}>
              {tweet.status !== 'posted' && (
                <>
                  <button className="btn btn-sm btn-success" onClick={() => handlePostNow(tweet)} disabled={postingId === tweet.id}>
                    {postingId === tweet.id ? '投稿中...' : '⚡ 今すぐ投稿'}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setScheduleTweet(tweet)}>
                    🗓️ スケジュール
                  </button>
                  <button className="btn btn-sm btn-ghost" onClick={() => setEditTweet(tweet)}>編集</button>
                </>
              )}
              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(tweet.id)}>削除</button>
            </div>
          </div>
        ))
      )}

      {showAdd && <TweetModal onClose={() => setShowAdd(false)} onSaved={loadTweets} />}
      {editTweet && <TweetModal tweet={editTweet} onClose={() => setEditTweet(null)} onSaved={loadTweets} />}
      {scheduleTweet && <ScheduleModal tweet={scheduleTweet} onClose={() => setScheduleTweet(null)} onScheduled={loadTweets} />}
    </div>
  );
}
